/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { SET_LINE_ITEM_NOTE_MUTATION } from './graphql/SetLineItemNoteMutation';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';

export interface SetLineItemNoteInput {
  quoteUid: string;
  itemUid: string;
  note: string;
  quantity?: number;
}

export const setLineItemNote = async (
  input: SetLineItemNoteInput
): Promise<NegotiableQuoteModel | null> => {
  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  const { quoteUid, itemUid, note, quantity } = input;

  if (!quoteUid) {
    throw new Error('Quote UID is required');
  }

  if (!itemUid) {
    throw new Error('Item UID is required');
  }

  if (!note) {
    throw new Error('Note is required');
  }

  // Build the mutation input
  const mutationInput: any = {
    quote_uid: quoteUid,
    quote_item_uid: itemUid,
    note: note.trim(),
  };

  return fetchGraphQl(SET_LINE_ITEM_NOTE_MUTATION, {
    variables: {
      input: mutationInput,
    },
  }).then((response) => {
    const { errors } = response;

    if (errors) {
      // Provide more detailed error information
      const errorMessages = errors.map((error) => error.message).join('; ');
      throw new Error(`Failed to set line item note: ${errorMessages}`);
    }

    // Transform the quote data
    const quoteData = transformQuote(response.data?.setLineItemNote?.quote);

    if (!quoteData) {
      throw new Error(
        'Failed to transform quote data: Invalid response structure'
      );
    }

    // Emit event with transformed quote data on success
    events.emit('quote-management/line-item-note-set', {
      quote: quoteData,
      input: { quoteUid, itemUid, note, quantity },
    });

    // Also emit the general quote-data event to update all listeners
    events.emit('quote-management/quote-data', {
      quote: quoteData,
      permissions: state.permissions,
    });

    return quoteData;
  });
};
